import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  Icon,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import DialogBase from "./DialogBase";
import ProgressBar from "../ProgressBar";
import ErrorMessage from "../ErrorMessage";

import { consortiumService } from "../../../services/consortium.service";
import { planManagementService } from "../../../services/plan-management.service";
import { authService } from "../../../services/auth.service";
import { userManagementService } from "../../../services/user-management.service";
import { Consortium } from "../../../models/consortium/consortium";
import { ListParams } from "../../../models/listParams";

interface DialogConsortiumMembersProps {
  open: boolean;
  consortiumId?: string | null;
  onClose: () => void;
  onUpdated: () => void;
  readOnly?: boolean;
  members?: { id: string; displayName?: string; name?: string }[];
}

interface OrganizationOption {
  id: string;
  label: string;
  group?: 'Met gebied' | 'Zonder gebied';
}

const REFRESH_DELAY_MS = 750;

const DialogConsortiumMembers: React.FC<DialogConsortiumMembersProps> = ({
  open,
  consortiumId,
  onClose,
  onUpdated,
  readOnly = false,
  members: initialMembers,
}) => {
  const [consortium, setConsortium] = useState<Consortium | null>(null);
  const [options, setOptions] = useState<OrganizationOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<OrganizationOption[]>([]);
  const [selectionError, setSelectionError] = useState<string | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [currentOrganisatieId, setCurrentOrganisatieId] = useState<string | null>(null);

  useEffect(() => {
    const checkRole = async () => {
      setIsAdmin(await authService.isAdmin());
      const storedOrg = userManagementService.getStoredOrganisatie();
      setCurrentOrganisatieId(storedOrg?.id ?? null);
    };
    checkRole();
  }, []);

  const loadData = useCallback(async () => {
    if (!consortiumId) return;

    setLoading(true);
    setError(null);

    try {
      let currentConsortium: Consortium;
      let allOrganizations: OrganizationOption[] = [];
      
      if (isAdmin) {
        const params: ListParams = { offset: 0, limit: 1000 };
        const [consortium, shareData] = await Promise.all([
          consortiumService.getConsortium(consortiumId),
          planManagementService.getOrganisatiesToShare(params, true),
        ]);

        currentConsortium = consortium;

        const allOrgs: any[] = Array.isArray(shareData?.items) ? shareData.items : [];
        allOrganizations = allOrgs
          .filter((org: any) => org?.id)
          .map((org: any) => ({
            id: org.id,
            label: org.displayName || org.name || org.id,
            group: org.organizationType === 'zonder-gebied' ? 'Zonder gebied' : 'Met gebied',
          } as OrganizationOption))
          .sort((a, b) => {
            if (a.group !== b.group) return (a.group ?? '').localeCompare(b.group ?? '', 'nl-NL');
            return a.label.localeCompare(b.label, 'nl-NL');
          });
      } else {
        // TODO: Later implementeren - portfolioService.getConsortium endpoint
        // await portfolioService.getConsortium(consortiumId);
        // Voor nu gebruiken we de members die meegegeven zijn
        currentConsortium = {
          id: consortiumId,
          name: '',
          members: initialMembers ?? [],
        };
      }
      setConsortium(currentConsortium);
      
      // For admins: set options to all organizations
      // For non-admins: options will be based on memberOptions computed value
      if (isAdmin) {
        setOptions(allOrganizations);
      }
      
      setSelectedOptions([]);
      setSelectionError(null);
    }
    catch (err: any) {
      setError(err?.message || "Er is een fout opgetreden");
    }
    finally {
      setLoading(false);
    }
  }, [consortiumId, isAdmin, initialMembers]);

  useEffect(() => {
    if (!open) return;
    loadData();
  }, [open, loadData]);

  const memberOptions = useMemo(() => {
    return (consortium?.members ?? [])
      .map((member: any) => ({
        id: member?.id,
        label: member?.displayName || member?.name || member?.id,
      }))
      .filter((member) => member?.id)
      .sort((a: OrganizationOption, b: OrganizationOption) => a.label.localeCompare(b.label, "nl-NL"));
  }, [consortium?.members]);

  const memberIdSet = useMemo(() => {
    return new Set((consortium?.members ?? [])
      .map((member: any) => member?.id)
      .filter(Boolean));
  }, [consortium?.members]);

  const selectableOptions = useMemo(() => {
    return options.filter((option) => !memberIdSet.has(option.id));
  }, [options, memberIdSet]);

  const refreshAfterMutation = useCallback(async () => {
    await new Promise((resolve) => setTimeout(resolve, REFRESH_DELAY_MS));
    await loadData();
    onUpdated();
  }, [loadData, onUpdated]);

  const addSelectedOrganizations = async () => {
    if (!consortiumId) return;

    if (selectedOptions.length === 0) {
      setSelectionError("Selecteer minimaal 1 organisatie.");
      return;
    }

    setSelectionError(null);

    setSaving(true);
    setError(null);

    try {
      const currentMemberIds = new Set((consortium?.members ?? []).map((m: any) => 
        typeof m === 'string' ? m : m?.id
      ).filter(Boolean));
      const idsToAdd = selectedOptions
        .map((item) => item.id)
        .filter((id) => !currentMemberIds.has(id));

      await Promise.all(idsToAdd.map((organizationId) => consortiumService.addOrganizationToConsortium(consortiumId, organizationId)));

      await refreshAfterMutation();
    }
    catch (err: any) {
      setError(err?.message || "Toevoegen van organisaties is mislukt");
    }
    finally {
      setSaving(false);
    }
  };

  const removeMember = async (organizationId: string) => {
    if (!consortiumId) return;

    setSaving(true);
    setError(null);

    try {
      await consortiumService.removeOrganizationFromConsortium(consortiumId, organizationId);
      await refreshAfterMutation();
    }
    catch (err: any) {
      setError(err?.message || "Verwijderen van organisatie is mislukt");
    }
    finally {
      setSaving(false);
    }
  };

  return (
    <DialogBase
      open={open}
      title={readOnly ? "Leden van samenwerkingsverband" : "Organisaties in samenwerkingsverband beheren"}
      contentText={readOnly ? "Overzicht van organisaties die aan dit samenwerkingsverband gekoppeld zijn." : "Voeg organisaties toe of verwijder bestaande organisaties uit dit samenwerkingsverband."}
      onClose={onClose}
      width="900px"
      minWidth="700px"
    >
      {!readOnly && (
        <>
          <FormControl fullWidth sx={{ my: 1 }}>
            <Autocomplete
              multiple
              options={selectableOptions}
              value={selectedOptions}
              noOptionsText="Geen keuzes beschikbaar"
              onChange={(_event, value) => {
                setSelectedOptions(value);
                if (value.length > 0) {
                  setSelectionError(null);
                }
              }}
              groupBy={(option) => option.group ?? 'Met gebied'}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterSelectedOptions
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="filled" label={option.label} {...getTagProps({ index })} key={option.id} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="Organisaties toevoegen"
                  variant="filled"
                  placeholder="Zoek en selecteer een of meerdere organisaties"
                  error={Boolean(selectionError)}
                  helperText={selectionError || undefined}
                />
              )}
            />
          </FormControl>

          <Box sx={{ display: "flex", my: 0.5 }}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Icon>add</Icon>}
              onClick={addSelectedOrganizations}
              disabled={saving || selectedOptions.length === 0}
              sx={{ width: "auto" }}
            >
              Toevoegen
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />
        </>
      )}

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Huidige organisaties ({memberOptions.length})
      </Typography>

      <List dense>
        {memberOptions.map((member) => {
          const isCurrentOrganisatie = member.id === currentOrganisatieId;
          return (
            <ListItem
              key={member.id}
              secondaryAction={
                !readOnly ? (
                  <Tooltip title="Organisatie verwijderen" placement="right" arrow>
                    <IconButton edge="end" onClick={() => removeMember(member.id)}>
                      <Icon>delete</Icon>
                    </IconButton>
                  </Tooltip>
                ) : undefined
              }
            >
              <ListItemText
                primary={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {member.label}
                    {isCurrentOrganisatie && (
                      <Chip
                        size="small"
                        label="Eigen organisatie"
                        variant="outlined"
                        color="primary"
                      />
                    )}
                  </div>
                }
              />
            </ListItem>
          );
        })}

        {memberOptions.length === 0 && (
          <ListItem>
            <ListItemText primary="Nog geen organisaties" />
          </ListItem>
        )}
      </List>

      {(loading || saving) && <ProgressBar />}
      {error && <ErrorMessage message={error} />}
    </DialogBase>
  );
};

export default DialogConsortiumMembers;
